import React from 'react';
import { DownloadIcon, NewspaperIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionHeading } from '../components/SectionHeading';
import { BOARD, HISTORY, IDENTITY_SYMBOLS, PRESS, TEACHERS } from '../data/club';
const SUB_NAV = [{
  id: 'histoire',
  label: 'L’histoire'
}, {
  id: 'valeurs',
  label: 'Les valeurs'
}, {
  id: 'enseignants',
  label: 'Les enseignants'
}, {
  id: 'bureau',
  label: 'Le bureau'
}, {
  id: 'presse',
  label: 'Dossier de presse'
}, {
  id: 'reglement',
  label: 'Règlement intérieur'
}];
export function Club() {
  return <>
      <PageHeader kanji="歴史" eyebrow="Le Club" title="Un dojo familial, une exigence partagée" intro="De sa création à aujourd’hui, le Zanshin Karaté Club cultive l’esprit associatif et l’ADN familial, au service d’une pratique exigeante et accessible." data-id="element-125" data-mp="1sdpi87ubsyjh" />

      <nav aria-label="Sections de la page" className="border-b border-gold/15 bg-panel" data-id="element-126" data-mp="2yu187lmzva3z">
        <ul className="mx-auto flex max-w-shell gap-x-6 gap-y-2 overflow-x-auto px-5 py-4 lg:px-8" data-id="element-127" data-mp="3dschdk3306gi">
          {SUB_NAV.map(item => <li key={item.id} data-id="element-128" data-mp="3ipw12zijtbfu">
              <a href={`#${item.id}`} className="zk-focus whitespace-nowrap font-display text-xs uppercase tracking-[0.16em] text-muted transition-colors duration-150 ease-out hover:text-gold" data-id="element-129" data-mp="3it0acbavexdd">
                {item.label}
              </a>
            </li>)}
        </ul>
      </nav>

      <section className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-130" data-mp="2zzwdyi69q6f2">
        <SectionHeading id="histoire" kanji="歴史" title="Notre histoire" description="Quatre étapes qui racontent la construction d’un club de village devenu une référence en Occitanie." data-id="element-131" data-mp="35029i9fnsd2f" />
        <ol className="mt-12 border-l border-gold/20 pl-6 sm:pl-10" data-id="element-132" data-mp="2ca2489h8wxz1">
          {HISTORY.map(step => <li key={step.year} className="relative pb-10 last:pb-0" data-id="element-133" data-mp="2h8wt9t5t3irl">
              <span aria-hidden className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 bg-crimson sm:-left-[47px]" data-id="element-134" data-mp="299079w6ds828" />
              <p className="font-display text-sm uppercase tracking-[0.2em] text-gold" data-id="element-135" data-mp="249dxxxtobbyp">{step.year}</p>
              <h3 className="mt-2 font-display text-xl text-bone" data-id="element-136" data-mp="2iya2vm0fo8v3">{step.title}</h3>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted" data-id="element-137" data-mp="2dzys992bdubt">{step.text}</p>
            </li>)}
        </ol>
      </section>

      <section className="border-y border-gold/15 bg-panel" data-id="element-138" data-mp="1qcqmyr4vyl9s">
        <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-139" data-mp="1lcmftrpvb2w8">
          <SectionHeading id="valeurs" kanji="価値" title="Nos valeurs" description="Le samouraï de notre logo incarne les sept valeurs du Bushido, transmises chaque jour sur le tatami et bien au-delà du dojo." data-id="element-140" data-mp="209d71dpe18ar" />
          <div className="mt-12 grid gap-10 lg:grid-cols-3" data-id="element-141" data-mp="3bptr5kbw0d3y">
            {IDENTITY_SYMBOLS.map(symbol => <article key={symbol.title} className="flex h-full flex-col border-t border-gold/25 pt-6" data-id="element-142" data-mp="3e8eyzxyjgckj">
                <span aria-hidden className="font-jp text-4xl text-crimson" data-id="element-143" data-mp="3j78efvxdydnc">
                  {symbol.kanji}
                </span>
                <h3 className="mt-4 font-display text-lg uppercase tracking-[0.06em] text-bone" data-id="element-144" data-mp="358s7c3vm2l4o">{symbol.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-145" data-mp="399uagvsnfhbx">{symbol.text}</p>
              </article>)}
          </div>
          <p className="mt-12 max-w-3xl border-l-2 border-gold pl-6 text-base leading-relaxed text-bone" data-id="element-146" data-mp="5slukhhherju">
            Notre ambition : former des pratiquants confiants, respectueux et persévérants, dans une ambiance
            harmonieuse où chacun progresse à son rythme.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-147" data-mp="jsmrxmyjfguk">
        <SectionHeading id="enseignants" kanji="先生" title="Les enseignants" description="Parcours, grades et diplômes de celles et ceux qui encadrent les cours tout au long de la saison." data-id="element-148" data-mp="jqju4gr58jfa" />
        <div className="mt-12 grid gap-8 md:grid-cols-2" data-id="element-149" data-mp="2s0tapsfsiruj">
          {TEACHERS.map(teacher => <article key={teacher.name} className="flex h-full flex-col border border-white/5 bg-panel sm:flex-row" data-id="element-150" data-mp="2n183yvudpf79">
              <img src={teacher.photo} alt={`Portrait de ${teacher.name}`} className="h-64 w-full object-cover object-top sm:h-auto sm:w-44" data-id="element-151" data-mp="2j1pqum8su8ez" />
              <div className="flex flex-1 flex-col p-6" data-id="element-152" data-mp="3arki4og7w86a">
                <h3 className="font-display text-xl text-bone" data-id="element-153" data-mp="3asvo8ux4xs0k">{teacher.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-gold" data-id="element-154" data-mp="35zsq09hexue1">{teacher.role}</p>
                {teacher.grade && <p className="mt-3 text-sm font-medium text-crimson" data-id="element-155" data-mp="31yr0ikdaswp9">{teacher.grade}</p>}
                <ul className="mt-4 space-y-2 text-sm text-muted" data-id="element-156" data-mp="2ckc9lnyu6cn">
                  {teacher.details.map(detail => <li key={detail} className="flex gap-2" data-id="element-157" data-mp="3tp59c26gfiu2">
                      <span aria-hidden className="mt-2 h-px w-3 shrink-0 bg-gold-dim" data-id="element-158" data-mp="1g8k6rkyyw5v7" />
                      {detail}
                    </li>)}
                </ul>
              </div>
            </article>)}
        </div>
      </section>

      <section className="border-y border-gold/15 bg-panel" data-id="element-159" data-mp="1u7k035xfd8xp">
        <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-160" data-mp="1u94saow9ofse">
          <SectionHeading id="bureau" kanji="役員" title="Le bureau" description="Une équipe de bénévoles qui fait vivre l’association au quotidien." data-id="element-161" data-mp="1z5lhznj4727l" />
          <ul className="mt-10 grid gap-px border border-gold/10 bg-gold/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-162" data-mp="28xr80rpt39k5">
            {BOARD.map(member => <li key={member.name} className="bg-panel p-6" data-id="element-163" data-mp="2hx5wxmqbrc2j">
                <p className="font-display text-base uppercase tracking-[0.06em] text-bone" data-id="element-164" data-mp="2hypfjjzgdz7h">{member.name}</p>
                <p className="mt-1 text-sm text-gold" data-id="element-165" data-mp="2mwih1zpta51f">{member.role}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-166" data-mp="hveq7fogyo10">
        <SectionHeading id="presse" kanji="新聞" title="Dossier de presse" description="Les articles consacrés au club, principalement dans La Dépêche du Midi." data-id="element-167" data-mp="wjsr3l2bs2ex" />
        <ul className="mt-10 divide-y divide-gold/10 border-y border-gold/10" data-id="element-168" data-mp="15k7ecpyunrl2">
          {PRESS.map(article => <li key={article.title} className="grid gap-3 py-6 md:grid-cols-[13rem_1fr] md:gap-10" data-id="element-169" data-mp="11jf7u4hd40sh">
              <div data-id="element-170" data-mp="1fiwykpe7xo4h">
                <p className="flex items-center gap-2 font-display text-xs uppercase tracking-[0.16em] text-gold" data-id="element-171" data-mp="1fhc4f0vtg775">
                  <NewspaperIcon size={14} aria-hidden data-id="element-172" data-mp="1akdaojg2e29h" />
                  {article.source}
                </p>
                <p className="mt-1 text-xs text-muted" data-id="element-173" data-mp="3hc4cbue6nxo9">{article.date}</p>
              </div>
              <div data-id="element-174" data-mp="3ccrnx57nyijk">
                <h3 className="font-display text-lg leading-snug text-bone" data-id="element-175" data-mp="3cd9vgxzs0j6s">{article.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted" data-id="element-176" data-mp="1rv8tc3hclpyn">{article.excerpt}</p>
              </div>
            </li>)}
        </ul>
      </section>

      <section id="reglement" className="border-t border-gold/15 bg-panel" data-id="element-177" data-mp="1i2tal3y88xcp">
        <div className="mx-auto flex max-w-shell flex-col gap-6 px-5 py-14 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-178" data-mp="1n0v4kjx6yh2y">
          <div data-id="element-179" data-mp="194ho9e4e8lq2">
            <h2 className="font-display text-2xl uppercase tracking-[0.06em] text-bone" data-id="element-180" data-mp="1e1hsdjhoneej">Règlement intérieur</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted" data-id="element-181" data-mp="z4h0nfzhrjg2">
              Tenue, ponctualité, respect du dojo et des partenaires, conditions de participation aux compétitions :
              le règlement s’applique à tous les adhérents et est remis lors de l’inscription.
            </p>
          </div>
          <a href="#reglement" className="zk-focus inline-flex items-center gap-2 border border-gold px-6 py-3 font-display text-sm font-semibold uppercase tracking-[0.14em] text-gold transition-colors duration-150 ease-out hover:bg-gold hover:text-ink" data-id="element-182" data-mp="2yhl4xmexoeon">
            <DownloadIcon size={16} aria-hidden data-id="element-183" data-mp="1chn0iqf71vre" />
            Télécharger le PDF
          </a>
        </div>
      </section>
    </>;
}